/**
 * iter395 — AdminAmbassadors: tab inside /admin for managing Ambassador
 * applications and viewing referral analytics.
 *
 * Backend:
 *   GET  /api/ambassadors/admin/list?status=...
 *   POST /api/ambassadors/{id}/decide  { decision: 'approve' | 'reject', reviewer_notes }
 *   GET  /api/ambassadors/admin/stats
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Stethoscope, Sparkles, Dumbbell, Heart, Check, X, Loader2, Copy, ExternalLink, RefreshCw, TrendingUp, Users, MousePointerClick,
} from "lucide-react";
import { toast } from "sonner";

const TIER_ICON = { medical: Stethoscope, influencer: Sparkles, coach: Dumbbell, patient_advocate: Heart };
const TIER_COLOR = { medical: "#287a2e", influencer: "#e44288", coach: "#5e227f", patient_advocate: "#0e5c60" };

const STATUS_LABEL = {
  pending_review: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};
const STATUS_COLOR = {
  pending_review: "#F59E0B",
  approved: "#10B981",
  rejected: "#EF4444",
};

function makeApiHeaders() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("nutramilo_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminAmbassadors({ API, isDark }) {
  const [tab, setTab] = useState("applications"); // applications | analytics
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [decisionInProgress, setDecisionInProgress] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState("");

  const cardBase = isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200";

  const fetchApps = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/ambassadors/admin/list?status=${statusFilter}`, { headers: makeApiHeaders() });
      setApplications(data?.items || []);
    } catch (e) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/ambassadors/admin/stats`, { headers: makeApiHeaders() });
      setStats(data?.items || []);
    } catch (e) {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "applications") fetchApps();
    else fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter]);

  const decide = async (decision) => {
    if (!selected) return;
    setDecisionInProgress(true);
    try {
      const { data } = await axios.post(
        `${API}/ambassadors/${selected.id}/decide`,
        { decision, reviewer_notes: reviewerNotes || null },
        { headers: makeApiHeaders() },
      );
      if (decision === "approve" && data?.ambassador?.referral_code) {
        toast.success(`Approved! Referral code: ${data.ambassador.referral_code}`);
      } else {
        toast.success(`Application ${decision}d`);
      }
      setSelected(null);
      setReviewerNotes("");
      await fetchApps();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    } finally {
      setDecisionInProgress(false);
    }
  };

  const copyReferralLink = (code) => {
    const link = `${window.location.origin}/?ref=${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };

  const totals = useMemo(() => {
    const visits = stats.reduce((acc, s) => acc + (s.visits_total || 0), 0);
    const signups = stats.reduce((acc, s) => acc + (s.signups_total || 0), 0);
    const paid = stats.reduce((acc, s) => acc + (s.paid_total || 0), 0);
    const mrr = stats.reduce((acc, s) => acc + (s.mrr_eur || 0), 0);
    return { visits, signups, paid, mrr };
  }, [stats]);

  return (
    <div data-testid="admin-ambassadors" className="space-y-4">
      {/* Sub-tab toggle */}
      <div className="flex gap-2 items-center">
        <button
          data-testid="admin-amb-tab-applications"
          onClick={() => setTab("applications")}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${tab === "applications" ? "bg-[#287a2e] text-white" : isDark ? "text-slate-300 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
        >
          Applications
        </button>
        <button
          data-testid="admin-amb-tab-analytics"
          onClick={() => setTab("analytics")}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${tab === "analytics" ? "bg-[#287a2e] text-white" : isDark ? "text-slate-300 hover:bg-white/5" : "text-slate-700 hover:bg-slate-100"}`}
        >
          Referral Analytics
        </button>
        <div className="flex-1" />
        <Button
          data-testid="admin-amb-refresh"
          variant="outline" size="sm"
          onClick={() => (tab === "applications" ? fetchApps() : fetchStats())}
          className="rounded-full"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {tab === "applications" && (
        <>
          {/* Status filter */}
          <div className="flex gap-2 flex-wrap">
            {["pending_review", "approved", "rejected", "all"].map((s) => (
              <button
                key={s}
                data-testid={`admin-amb-filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${statusFilter === s ? "bg-[#287a2e] text-white border-transparent" : isDark ? "bg-transparent text-slate-300 border-slate-700" : "bg-white text-slate-700 border-slate-200"} border`}
              >
                {STATUS_LABEL[s] || (s === "all" ? "All" : s)}
              </button>
            ))}
          </div>

          {/* List */}
          {loading && (
            <div className="text-center py-8 text-sm text-slate-500"><Loader2 className="inline w-4 h-4 mr-1 animate-spin" /> Loading…</div>
          )}
          {!loading && applications.length === 0 && (
            <div className={`text-center py-12 rounded-xl border ${cardBase}`}>
              <p className="text-sm text-slate-500">No applications with status: {STATUS_LABEL[statusFilter] || statusFilter}</p>
            </div>
          )}
          <div className="space-y-2" data-testid="admin-amb-list">
            {applications.map((app) => {
              const Icon = TIER_ICON[app.tier_interest] || Stethoscope;
              const color = TIER_COLOR[app.tier_interest] || "#287a2e";
              return (
                <div
                  key={app.id}
                  data-testid={`admin-amb-row-${app.id}`}
                  className={`rounded-xl border p-4 flex items-start gap-3 ${cardBase}`}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{app.full_name}</h4>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${STATUS_COLOR[app.status] || "#64748b"}20`, color: STATUS_COLOR[app.status] || "#64748b" }}>
                        {STATUS_LABEL[app.status] || app.status}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color }}>
                        {app.tier_interest}
                      </span>
                    </div>
                    <div className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"} mb-1`}>
                      {app.email} · {app.country_code} · {app.profession}
                    </div>
                    {app.referral_code && (
                      <div className="text-xs font-mono mt-1" style={{ color: "#287a2e" }}>
                        ref: {app.referral_code}
                        <button onClick={() => copyReferralLink(app.referral_code)} className="ml-2 text-slate-500 hover:text-slate-700">
                          <Copy className="w-3 h-3 inline" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
                    <a
                      href={app.primary_channel_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs underline text-slate-500"
                    >
                      Channel <ExternalLink className="w-3 h-3 inline" />
                    </a>
                    <Button
                      size="sm" variant="outline"
                      data-testid={`admin-amb-view-${app.id}`}
                      onClick={() => setSelected(app)}
                      className="text-xs h-7 rounded-full"
                    >
                      View
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "analytics" && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className={`rounded-xl border p-4 ${cardBase}`}>
              <MousePointerClick className="w-4 h-4 mb-1" style={{ color: "#0EA5E9" }} />
              <div className="text-2xl font-bold">{totals.visits}</div>
              <div className="text-xs text-slate-500">Visits</div>
            </div>
            <div className={`rounded-xl border p-4 ${cardBase}`}>
              <Users className="w-4 h-4 mb-1" style={{ color: "#287a2e" }} />
              <div className="text-2xl font-bold">{totals.signups}</div>
              <div className="text-xs text-slate-500">Signups</div>
            </div>
            <div className={`rounded-xl border p-4 ${cardBase}`}>
              <Check className="w-4 h-4 mb-1" style={{ color: "#10B981" }} />
              <div className="text-2xl font-bold">{totals.paid}</div>
              <div className="text-xs text-slate-500">Paid conversions</div>
            </div>
            <div className={`rounded-xl border p-4 ${cardBase}`}>
              <TrendingUp className="w-4 h-4 mb-1" style={{ color: "#e44288" }} />
              <div className="text-2xl font-bold">€{totals.mrr.toFixed(2)}</div>
              <div className="text-xs text-slate-500">MRR from referrals</div>
            </div>
          </div>

          {loading && <div className="text-center py-8 text-sm text-slate-500"><Loader2 className="inline w-4 h-4 mr-1 animate-spin" /> Loading…</div>}
          {!loading && stats.length === 0 && (
            <div className={`text-center py-12 rounded-xl border ${cardBase}`}>
              <p className="text-sm text-slate-500">No approved ambassadors yet. Approve applications to start tracking referrals.</p>
            </div>
          )}

          {/* Per-ambassador table */}
          {stats.length > 0 && (
            <div className={`rounded-xl border overflow-hidden ${cardBase}`}>
              <table className="w-full text-sm">
                <thead className={isDark ? "bg-[hsl(223,47%,12%)]" : "bg-slate-50"}>
                  <tr>
                    <th className="text-left p-3 text-xs font-semibold">Ambassador</th>
                    <th className="text-left p-3 text-xs font-semibold">Tier</th>
                    <th className="text-left p-3 text-xs font-semibold">Ref code</th>
                    <th className="text-right p-3 text-xs font-semibold">Visits</th>
                    <th className="text-right p-3 text-xs font-semibold">Signups</th>
                    <th className="text-right p-3 text-xs font-semibold">Paid</th>
                    <th className="text-right p-3 text-xs font-semibold">MRR</th>
                    <th className="text-right p-3 text-xs font-semibold">Commission %</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((amb) => (
                    <tr key={amb.id} data-testid={`admin-amb-stat-${amb.referral_code}`} className={isDark ? "border-t border-[hsl(223,47%,15%)]" : "border-t border-slate-100"}>
                      <td className="p-3">
                        <div className={`text-xs font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{amb.full_name}</div>
                        <div className="text-[10px] text-slate-500">{amb.email}</div>
                      </td>
                      <td className="p-3 text-xs">{amb.tier}</td>
                      <td className="p-3">
                        <span className="text-xs font-mono">{amb.referral_code}</span>
                        <button onClick={() => copyReferralLink(amb.referral_code)} className="ml-1.5 text-slate-400 hover:text-slate-700">
                          <Copy className="w-3 h-3 inline" />
                        </button>
                      </td>
                      <td className="p-3 text-right text-xs">{amb.visits_total || 0}</td>
                      <td className="p-3 text-right text-xs">{amb.signups_total || 0}</td>
                      <td className="p-3 text-right text-xs">{amb.paid_total || 0}</td>
                      <td className="p-3 text-right text-xs">€{(amb.mrr_eur || 0).toFixed(2)}</td>
                      <td className="p-3 text-right text-xs">{amb.commission_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-amb-modal" onClick={() => !decisionInProgress && setSelected(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto ${isDark ? "bg-[hsl(223,47%,9%)]" : "bg-white"} p-6`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold mb-1">{selected.full_name}</h3>
                <div className="text-xs text-slate-500">
                  {selected.email} · {selected.country_code} · Tier: <span className="font-semibold">{selected.tier_interest}</span>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Profession</div>
                <div>{selected.profession}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Primary channel</div>
                <a href={selected.primary_channel_url} target="_blank" rel="noreferrer" className="underline text-[#287a2e]">{selected.primary_channel_url}</a>
                {selected.audience_size != null && <span className="text-xs text-slate-500 ml-2">· {selected.audience_size.toLocaleString()} followers</span>}
              </div>
              {selected.secondary_channel_url && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Secondary channel</div>
                  <a href={selected.secondary_channel_url} target="_blank" rel="noreferrer" className="underline text-[#287a2e]">{selected.secondary_channel_url}</a>
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Audience</div>
                <div className="text-slate-700 whitespace-pre-wrap">{selected.audience_description}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Why Nutramilo</div>
                <div className="text-slate-700 whitespace-pre-wrap">{selected.why_nutramilo}</div>
              </div>

              <div className="pt-3 border-t border-slate-200">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Reviewer notes (optional)</label>
                <textarea
                  data-testid="admin-amb-notes"
                  value={reviewerNotes}
                  onChange={(e) => setReviewerNotes(e.target.value)}
                  rows={2}
                  className={`w-full p-2 text-sm rounded-lg border ${isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)] text-slate-200" : "border-slate-200 text-slate-700"}`}
                  placeholder="Internal notes for this decision…"
                />
              </div>

              {selected.status === "pending_review" ? (
                <div className="flex gap-2 pt-2">
                  <Button
                    data-testid="admin-amb-approve"
                    onClick={() => decide("approve")}
                    disabled={decisionInProgress}
                    className="flex-1 bg-[#287a2e] hover:bg-[#1f5f23] text-white rounded-full h-10"
                  >
                    {decisionInProgress ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Approve (issue referral code)
                  </Button>
                  <Button
                    data-testid="admin-amb-reject"
                    onClick={() => decide("reject")}
                    disabled={decisionInProgress}
                    variant="outline"
                    className="flex-1 rounded-full h-10"
                  >
                    {decisionInProgress ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                    Reject
                  </Button>
                </div>
              ) : (
                <div className="pt-2 text-xs text-slate-500">
                  Already {STATUS_LABEL[selected.status]} {selected.reviewed_by ? `by ${selected.reviewed_by}` : ""}
                  {selected.referral_code && <div className="mt-1 font-mono text-[#287a2e]">ref: {selected.referral_code}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
